import DataGrid, {
  Column,
  ColumnChooser,
  Grouping,
  GroupPanel,
  HeaderFilter,
  Item,
  Lookup,
  Paging,
  RequiredRule,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { RowPreparedEvent, RowUpdatingEvent } from 'devextreme/ui/data_grid';
import { getActivePresenceAvatarColor } from '../realtime/presenceAvatarColors';
import { OPS_SITE_GRID_PROPS, OpsGridStateStoring } from '../opsDataGrid';
import type { TrackerBoardLiveData } from './useLiveRows';
import { usePatientTrackingCsvImport } from './PatientTrackingCsvImportButton';
import { PatientTrackingGridToolbarItems } from './PatientTrackingGridToolbar';
import { type PatientTrackingApi, patientTrackingApi } from './patientTrackingApi';
import {
  PATIENT_STATUS_LABELS,
  PATIENT_STATUS_OPTIONS,
  type CoveredEntityDto,
  type PatientStatus,
  type PatientTrackerFieldPolicy,
  type PatientTrackingCsvImportConfig,
  type TrackerEntryDto,
  type TrackerEntryUpsertBody
} from './patientTrackingTypes';
import { trackerEntryToBody } from './trackerEntryEditorUtils';
import './patientTracking.css';

const PATIENT_TRACKER_GRID_SCHEMA_VERSION = 1;
const PATIENT_TRACKER_GRID_COLUMNS = [
  'status',
  'pharmacyName',
  'patientName',
  'patientDob',
  'coveredEntityId',
  'currentIssue',
  'enrollmentForm',
  'emailsSent',
  'emailLastSent',
  'nextAlertDate',
  'modified',
  'modifiedBy'
] as const;

export interface PatientTrackerGridProps {
  coveredEntities: CoveredEntityDto[];
  onPatientsChanged: () => void;
  currentUserId: string;
  currentUserName?: string;
  currentUserAliases?: Iterable<string>;
  stateStorageKey?: string;
  csvImport?: PatientTrackingCsvImportConfig;
  api?: PatientTrackingApi;
  onOpenView: (row: TrackerEntryDto) => void;
  onOpenCreate: () => void;
  fieldPolicy?: PatientTrackerFieldPolicy;
  live: TrackerBoardLiveData;
}

export function PatientTrackerGrid({
  coveredEntities,
  onPatientsChanged,
  currentUserId,
  stateStorageKey = 'patient-tracker-grid',
  csvImport,
  api = patientTrackingApi,
  onOpenView,
  onOpenCreate,
  fieldPolicy,
  live
}: PatientTrackerGridProps) {
  const allowCreate = fieldPolicy?.allowCreate !== false;
  const { rows, loading, error, reload, applyUpsert, rowEditors, setListEditing } = live;

  const gridRef = useRef<DataGridRef>(null);
  const [listEditMode, setListEditMode] = useState(false);

  const ceOptions = useMemo(
    () => coveredEntities.map((c) => ({ id: c.id, name: c.coveredEntityName })),
    [coveredEntities]
  );

  const toggleListEdit = useCallback(() => {
    setListEditMode((prev) => {
      const next = !prev;
      setListEditing(next);
      return next;
    });
  }, [setListEditing]);

  const onRowPrepared = (e: RowPreparedEvent<TrackerEntryDto, string>) => {
    if (e.rowType !== 'data' || !e.data) return;
    const editor = rowEditors.get(e.data.id);
    if (editor && editor.userId.toLowerCase() !== currentUserId.toLowerCase()) {
      const color = getActivePresenceAvatarColor(editor.userId);
      e.rowElement.classList.add('patient-tracker-row--locked');
      e.rowElement.style.setProperty('--pt-lock-color', color);
      e.rowElement.title = `Being edited by ${editor.displayName}`;
    }
  };

  const onRowUpdating = (e: RowUpdatingEvent<TrackerEntryDto, string>) => {
    const merged = { ...e.oldData, ...e.newData } as TrackerEntryDto;
    e.cancel = (async () => {
      try {
        const enrollmentChanged = Object.prototype.hasOwnProperty.call(e.newData ?? {}, 'enrollmentForm');
        const body: TrackerEntryUpsertBody = {
          ...trackerEntryToBody(merged),
          enrollmentForm: enrollmentChanged ? merged.enrollmentForm : null
        };
        const dto = await api.updateTrackerEntry(merged.id, body);
        applyUpsert(dto);
        if (enrollmentChanged) onPatientsChanged();
        return false;
      } catch (err) {
        window.alert(err instanceof Error ? err.message : 'Update failed.');
        return true;
      }
    })();
  };

  const csvImportUi = usePatientTrackingCsvImport({
    config: csvImport,
    disabled: loading,
    onImported: () => {
      void reload();
      onPatientsChanged();
    }
  });

  return (
    <>
      <DataGrid
        ref={gridRef}
        dataSource={rows}
        keyExpr="id"
        height="100%"
        width="100%"
        showBorders
        columnAutoWidth
        columnHidingEnabled
        allowColumnResizing
        allowColumnReordering
        columnResizingMode="widget"
        {...OPS_SITE_GRID_PROPS}
        noDataText={
          error ??
          (loading ? 'Loading…' : allowCreate ? 'No tracker entries yet — click Add issue to create one.' : 'No tracker entries yet.')
        }
        onRowDblClick={(e) => {
          e.event?.stopPropagation();
          if (!listEditMode && e.data) onOpenView(e.data);
        }}
        onRowPrepared={onRowPrepared}
        onRowUpdating={onRowUpdating}
        editing={{
          mode: 'row',
          allowUpdating: listEditMode,
          allowAdding: false,
          allowDeleting: false,
          useIcons: true
        }}
      >
        <OpsGridStateStoring
          storageKey={stateStorageKey}
          schemaVersion={PATIENT_TRACKER_GRID_SCHEMA_VERSION}
          columns={PATIENT_TRACKER_GRID_COLUMNS}
        />
        <Toolbar>
          <Item location="before" name="groupPanel" />
          <PatientTrackingGridToolbarItems
            gridRef={gridRef}
            loading={loading}
            onRefresh={() => void reload()}
            editItem={{
              hint: listEditMode ? 'Done editing list' : 'Edit list',
              active: listEditMode,
              onClick: toggleListEdit
            }}
            newItem={allowCreate ? { hint: 'Add issue', onClick: onOpenCreate } : undefined}
            uploadSlot={csvImportUi.uploadToolbarItem}
            uploadMenuItem={csvImportUi.uploadMenuItem}
            export={{
              fileName: 'ce-patient-tracking',
              worksheetName: 'Tracker',
              hint: 'Export tracker grid'
            }}
          />
          <Item name="searchPanel" location="after" />
        </Toolbar>
        <GroupPanel visible />
        <Grouping autoExpandAll />
        <SearchPanel visible width={240} placeholder="Search tracker…" />
        <HeaderFilter visible />
        <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
        <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
        <Paging defaultPageSize={50} />

        <Column dataField="status" caption="Status" groupIndex={0} minWidth={120}>
          <Lookup dataSource={PATIENT_STATUS_OPTIONS} valueExpr="value" displayExpr="label" />
          <RequiredRule />
        </Column>
        {!fieldPolicy?.hidePharmacy ? (
          <Column dataField="pharmacyName" caption="Pharmacy" minWidth={160} allowEditing={false} />
        ) : null}
        <Column dataField="patientName" caption="Patient" minWidth={170} allowEditing={false} />
        <Column dataField="patientDob" caption="DOB" dataType="date" width={110} allowEditing={false} />
        {!fieldPolicy?.hideCoveredEntity ? (
          <Column dataField="coveredEntityId" caption="CE to Alert" minWidth={150}>
            <Lookup dataSource={ceOptions} valueExpr="id" displayExpr="name" />
            <RequiredRule />
          </Column>
        ) : null}
        <Column dataField="currentIssue" caption="Current Issue" minWidth={220}>
          <RequiredRule />
        </Column>
        <Column dataField="enrollmentForm" caption="Enroll. Form" dataType="boolean" width={110} />
        <Column dataField="emailsSent" caption="Emails Sent" dataType="number" width={110} allowEditing={false} />
        <Column dataField="emailLastSent" caption="Last Sent" dataType="datetime" width={160} allowEditing={false} />
        <Column dataField="nextAlertDate" caption="Next Alert" dataType="datetime" width={160} allowEditing={false} />
        <Column
          dataField="modified"
          caption="Modified"
          dataType="datetime"
          width={160}
          allowEditing={false}
          visible={false}
        />
        <Column
          dataField="modifiedBy"
          caption="Modified By"
          minWidth={130}
          allowEditing={false}
          visible={false}
        />
      </DataGrid>

      {csvImportUi.importPopup}
    </>
  );
}

export const patientStatusLabel = (s: PatientStatus): string => PATIENT_STATUS_LABELS[s];
